import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import useSeo from '../hooks/useSeo';
import {
  Shield, Database, Lock, Users, Mail, Globe, CreditCard,
  Bot, MessageSquare, Eye, Cookie, Baby, FileText, Trash2, Bell, Briefcase
} from 'lucide-react';

const Section = ({ icon: Icon, number, title, children }) => (
  <div className="mb-10" data-testid={`privacy-section-${number}`}>
    <div className="flex items-center gap-3 mb-4">
      <div className="w-9 h-9 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
        <Icon className="w-[18px] h-[18px] text-green-700" />
      </div>
      <h2 className="text-lg sm:text-xl font-bold text-gray-900">{number}. {title}</h2>
    </div>
    <div className="pl-12 text-gray-700 leading-relaxed space-y-3 text-sm sm:text-base">{children}</div>
  </div>
);

const PrivacyPolicy = () => {
  useSeo({
    title: 'Privacy Policy — KnowMySchemes',
    description: 'KnowMySchemes Privacy Policy — how we collect, store, and protect your data. Read about your rights, data retention, and the anonymous training-data opt-out.',
    canonical: 'https://knowmyschemes.com/privacy',
  });
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-3xl mx-auto px-4 py-12 sm:py-16">
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-green-100 rounded-full mb-4">
            <Shield className="w-7 h-7 text-green-700" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2" data-testid="privacy-title">Privacy Policy</h1>
          <p className="text-gray-500 text-sm">Last updated: 17 April 2026 | Effective Date: 17 April 2026</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6 sm:p-8" data-testid="privacy-content">
          <p className="text-gray-700 mb-10 leading-relaxed text-sm sm:text-base">
            KnowMySchemes ("KMS", "we", "us") is committed to protecting your privacy and being fully transparent about how we handle your personal information. This policy explains what we collect, how we use it, and your rights as a user of our platform.
          </p>

          <Section icon={Database} number={1} title="Information We Collect">
            <p>We collect the following information when you create an account or use our services:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li><strong>Name and phone number</strong> — for account registration and OTP verification</li>
              <li><strong>Email address</strong> — optional, used for payment receipts and notifications if provided</li>
              <li><strong>Location and state preferences</strong> — for personalised scheme matching</li>
              <li><strong>Occupation and income details</strong> — for scheme eligibility matching</li>
              <li><strong>Profile descriptions entered in KMS Smart</strong> — for AI-powered analysis</li>
              <li><strong>Scheme browsing and search history</strong> — to improve recommendations</li>
              <li><strong>Device and platform information</strong> — website, iOS, or Android</li>
              <li><strong>Payment transaction references</strong> — for subscription management only</li>
            </ul>
            <p className="mt-3 font-medium text-gray-800">
              We do NOT collect Aadhaar numbers, PAN numbers, bank account details, or any government ID information.
            </p>
          </Section>

          <Section icon={Users} number={2} title="How We Use Your Data">
            <p>Your data is used exclusively to:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Match you with government schemes you are eligible for</li>
              <li>Generate personalised AI-powered policy impact analysis via KMS Smart</li>
              <li>Provide income intelligence recommendations based on your occupation and location</li>
              <li>Send relevant notifications about new schemes and policy updates</li>
              <li>Process subscription payments securely</li>
              <li>Improve our platform, scheme database, and AI accuracy</li>
              <li>Maintain account security and prevent fraud</li>
            </ul>
          </Section>

          <Section icon={MessageSquare} number={3} title="OTP Verification & Communication">
            <ul className="list-disc pl-5 space-y-1.5">
              <li>We use OTP-based login to verify your phone number and secure your account</li>
              <li>OTPs are sent via registered SMS infrastructure compliant with TRAI DLT regulations</li>
              <li>OTPs expire automatically and are never stored after verification</li>
              <li>We will <strong>NEVER</strong> ask for your OTP over call, email, or chat</li>
              <li>If you have provided your email address we may send payment receipts, subscription confirmations, and scheme alerts to your email</li>
            </ul>
          </Section>

          <Section icon={Bot} number={4} title="Anonymous Training Data">
            <p>
              With your consent, KnowMySchemes may collect anonymous usage data from KMS Smart analyses to improve our AI systems. This data includes occupation type, location, language preference, and policy events selected — never your name, phone number, or any personally identifiable information.
            </p>
            <p>
              You may opt out of this at any time through your profile privacy settings without affecting your access to any features.
            </p>
          </Section>

          <Section icon={Briefcase} number={5} title="Government Jobs Data">
            <p>KnowMySchemes aggregates government job notifications from publicly available official government recruitment portals for informational purposes only.</p>
            <ul className="list-disc pl-5 space-y-1.5 mt-2">
              <li>Job data is sourced from UPSC, SSC, Railway Recruitment Boards, APPSC, TSPSC, KPSC, IBPS, Employment News, NCS Portal, and other official government recruitment websites</li>
              <li>All data displayed is publicly available information intended for general citizen awareness</li>
              <li>No personal user data is shared with government recruitment portals through the jobs listing feature</li>
              <li>Job application links redirect to official government websites — KnowMySchemes does not collect or process any job application data</li>
              <li>Job listing data is refreshed periodically and may not reflect real-time changes made by government authorities</li>
              <li>KnowMySchemes does not store any data you enter on external government recruitment portals</li>
            </ul>
          </Section>

          <Section icon={Lock} number={6} title="Data Storage & Security">
            <p>Your data is stored securely with the following protections:</p>
            <ul className="space-y-2 mt-2">
              <li className="flex items-start gap-2"><Lock className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" /> <span>256-bit SSL encryption on all connections (HTTPS)</span></li>
              <li className="flex items-start gap-2"><Shield className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" /> <span>Encrypted storage in secure cloud databases</span></li>
              <li className="flex items-start gap-2"><MessageSquare className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" /> <span>OTP-based secure authentication</span></li>
              <li className="flex items-start gap-2"><Eye className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" /> <span>Strict access controls — only authorised systems access your data</span></li>
              <li className="flex items-start gap-2"><FileText className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" /> <span>Regular security audits and monitoring</span></li>
            </ul>
            <p className="mt-3">
              We retain your data for as long as your account is active. You may request deletion at any time by contacting <a href="mailto:support@knowmyschemes.com" className="text-green-700 hover:text-green-800 underline font-medium">support@knowmyschemes.com</a>.
            </p>
          </Section>

          <Section icon={Globe} number={7} title="Third Party Services">
            <p>We use trusted third-party services to operate KnowMySchemes. These services are bound by strict data protection agreements:</p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li><strong>Payment Processor</strong> — Handles subscription payments securely. We never store your card or banking information</li>
              <li><strong>SMS Service Provider</strong> — Delivers OTP messages to your phone number for login verification only</li>
              <li><strong>AI Analysis Engine</strong> — Powers KMS Smart personalised analysis. Your profile text is processed for analysis only and is not stored or used to train external AI models</li>
              <li><strong>News Data Provider</strong> — Supplies government policy news and budget updates. No personal data is shared</li>
              <li><strong>Push Notification Service</strong> — Delivers scheme alerts to your mobile device</li>
            </ul>
            <p className="mt-3 text-gray-500 text-sm italic">
              We do not mention specific provider names in this policy as these may change. All providers are bound by equivalent data protection standards.
            </p>
          </Section>

          <Section icon={CreditCard} number={8} title="We Do Not Sell Your Data">
            <p>
              We do not sell, rent, trade, or share your personal information with any third party for marketing, advertising, or any commercial purpose. Your data is used solely to provide and improve the KnowMySchemes service.
            </p>
          </Section>

          <Section icon={Cookie} number={9} title="Cookies & Tracking">
            <p>
              We use essential cookies to maintain your login session and app preferences. We do not use tracking cookies or advertising cookies. You may manage cookie preferences through your browser settings.
            </p>
          </Section>

          <Section icon={Baby} number={10} title="Children's Privacy">
            <p>
              KnowMySchemes is intended for users aged 18 and above. We do not knowingly collect personal information from anyone under 18 years of age. If you are under 18 please use the platform only with valid parental or guardian consent.
            </p>
          </Section>

          <Section icon={Eye} number={11} title="Your Rights">
            <p>Under the Digital Personal Data Protection Act 2023 (DPDP) and applicable Indian law you have the right to:</p>
            <ul className="space-y-1.5 mt-2">
              <li className="flex items-start gap-2"><span className="text-green-600 font-bold">&#10003;</span> <span>Access the personal data we hold about you</span></li>
              <li className="flex items-start gap-2"><span className="text-green-600 font-bold">&#10003;</span> <span>Request correction of inaccurate or incomplete data</span></li>
              <li className="flex items-start gap-2"><span className="text-green-600 font-bold">&#10003;</span> <span>Request deletion of your account and all associated data</span></li>
              <li className="flex items-start gap-2"><span className="text-green-600 font-bold">&#10003;</span> <span>Opt out of anonymous training data collection at any time</span></li>
              <li className="flex items-start gap-2"><span className="text-green-600 font-bold">&#10003;</span> <span>Opt out of SMS scheme alerts (OTP messages cannot be opted out for security)</span></li>
              <li className="flex items-start gap-2"><span className="text-green-600 font-bold">&#10003;</span> <span>Opt out of email communications at any time</span></li>
              <li className="flex items-start gap-2"><span className="text-green-600 font-bold">&#10003;</span> <span>Withdraw consent for any data processing activity</span></li>
              <li className="flex items-start gap-2"><span className="text-green-600 font-bold">&#10003;</span> <span>Obtain a copy of your data in a portable format</span></li>
            </ul>
            <p className="mt-3">
              All requests will be addressed within 30 days. Submit requests to <a href="mailto:support@knowmyschemes.com" className="text-green-700 hover:text-green-800 underline font-medium">support@knowmyschemes.com</a>
            </p>
          </Section>

          <Section icon={Trash2} number={12} title="Data Deletion">
            <p>You may delete your account at any time from your profile settings. Upon deletion:</p>
            <ul className="list-disc pl-5 space-y-1.5 mt-2">
              <li>All personal data is permanently removed within 7 days</li>
              <li>Anonymous aggregated data may be retained for statistical purposes only</li>
              <li>Subscription and payment records are retained as required by Indian financial regulations</li>
              <li>A confirmation email will be sent upon successful deletion</li>
            </ul>
          </Section>

          <Section icon={Bell} number={13} title="Changes to This Policy">
            <p>
              We may update this privacy policy from time to time. When we make significant changes we will notify you via SMS or email if provided. Continued use of KnowMySchemes after updates constitutes acceptance of the revised policy. The latest version is always available at <a href="https://knowmyschemes.com/privacy" className="text-green-700 hover:text-green-800 underline font-medium">knowmyschemes.com/privacy</a>
            </p>
          </Section>

          <Section icon={Mail} number={14} title="Contact & Grievance">
            <p>For privacy concerns, data requests, or complaints:</p>
            <div className="mt-3 space-y-2">
              <p className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-green-600 flex-shrink-0" />
                <span>Privacy: <a href="mailto:support@knowmyschemes.com" className="text-green-700 hover:text-green-800 underline font-medium">support@knowmyschemes.com</a></span>
              </p>
              <p className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-green-600 flex-shrink-0" />
                <span>Grievance: <a href="mailto:info@knowmyschemes.com" className="text-green-700 hover:text-green-800 underline font-medium">info@knowmyschemes.com</a></span>
              </p>
              <p className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-green-600 flex-shrink-0" />
                <span>Website: <a href="https://www.knowmyschemes.com" className="text-green-700 hover:text-green-800 underline font-medium">www.knowmyschemes.com</a></span>
              </p>
              <p className="flex items-center gap-2">
                <span className="w-4 h-4 flex items-center justify-center text-green-600 flex-shrink-0 font-bold text-xs">&#9201;</span>
                <span>Response time: Within 30 days</span>
              </p>
            </div>
          </Section>

          <div className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-500 text-center">
              &copy; {new Date().getFullYear()} KnowMySchemes. All rights reserved.
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
